import { defineComponent } from '@nuxtjs/composition-api';
import PublicProfileQuery from '@/components/partials/optional-anonymity/ProfileSelector/apollo/publicProfile.query';
import LastProfileUsedQuery from '@/components/partials/optional-anonymity/ProfileSelector/apollo/lastProfileUsed.query';
import AnonymousProfileQuery from '@/components/partials/optional-anonymity/ProfileSelector/apollo/anonymousProfile.query';
import { OPTIONAL_ANONYMITY_LEARN_MORE_TOOLTIP } from '@/components/partials/optional-anonymity/constants';
import { Activity } from '@/types/graphql';
import type { Profile } from '@/components/partials/optional-anonymity/ProfileSelector/types';
import { SelectorType } from '@/components/partials/optional-anonymity/ProfileSelector/types';
import { SourceAction } from '@/components/partials/war-room/WarRoomSignUp/types';
import type { WarRoomModalParams } from '@/components/partials/war-room/WarRoomSignUp/types';
import type { PublicPortfolioCurrentUser, CurrentUser } from '@/types/graphql';

export default defineComponent({
  name: 'ProfileSelectorMixin',
  props: {
    type: {
      type: String,
      default: SelectorType.post,
    },
  },
  apollo: {
    lastProfileUsed: {
      query: LastProfileUsedQuery,
      skip() {
        return !this.$auth.loggedIn;
      },
      update({ lastProfileUsed }: { lastProfileUsed: Activity }) {
        return this.isDisabled ? this.editProfileType : lastProfileUsed;
      },
      fetchPolicy: 'network-only',
    },
    public: {
      query: PublicProfileQuery,
      skip() {
        return !this.$auth.loggedIn;
      },
    },
    anonymous: {
      query: AnonymousProfileQuery,
      skip() {
        return !this.$auth.loggedIn;
      },
    },
  },
  data(): {
    lastProfileUsed: Activity;
    isDropdownVisible: boolean;
    public: PublicPortfolioCurrentUser | null;
    anonymous: CurrentUser | null;
  } {
    return {
      lastProfileUsed: Activity.Anonymous,
      isDropdownVisible: false,
      public: null,
      anonymous: null,
    };
  },
  computed: {
    isLoading() {
      return this.$apollo.loading;
    },
    isAnonymousProfileActive() {
      return this.lastProfileUsed === Activity.Anonymous;
    },
    hasAnonymousProfile() {
      return !!this.anonymous?.username;
    },
    isNewLabelShown() {
      return !this.hasAnonymousProfile || !this.$tooltip.isPresent(OPTIONAL_ANONYMITY_LEARN_MORE_TOOLTIP);
    },
    publicProfile(): Profile {
      return {
        name: 'Professional profile',
        type: Activity.Public,
        isSelected: !this.isAnonymousProfileActive || !this.hasAnonymousProfile,
        slug: this.public?.firstname ?? '',
        fullName: `${this.public?.firstname} ${this.public?.lastname}` ?? '',
        avatarURL: this.public?.avatarUrl ?? null,
        active: !!this.public?.configuration?.isPublic,
      };
    },
    anonymousProfile(): Profile {
      return {
        name: 'Anonymous profile',
        type: Activity.Anonymous,
        isSelected: this.isAnonymousProfileActive && this.hasAnonymousProfile,
        slug: this.anonymous?.username ?? '',
        fullName: this.anonymous?.username ?? '',
        avatarURL: this.anonymous?.warRoomAvatarUrl ?? null,
        active: true,
      };
    },
    currentProfile(): Profile {
      return this.isAnonymousProfileActive && this.hasAnonymousProfile ? this.anonymousProfile : this.publicProfile;
    },
    profileCreationParams(): WarRoomModalParams {
      const result = {
        source: SourceAction.common,
      };

      if (this.type === SelectorType.post) {
        result.source = SourceAction.post;
      }

      if (this.type === SelectorType.comment) {
        result.source = SourceAction.comment;
      }

      return result;
    },
  },
  watch: {
    currentProfile: {
      immediate: true,
      handler(newValue: Profile) {
        this.$emit('select', { isAnonymous: newValue.type === Activity.Anonymous, profile: this.currentProfile });
      },
    },
  },
  methods: {
    toggleDropdown() {
      if (!this.$tooltip.isPresent(OPTIONAL_ANONYMITY_LEARN_MORE_TOOLTIP)) {
        this.onTipsShown();
        return;
      }

      this.isDropdownVisible = !this.isDropdownVisible;
    },
    closeDropdown() {
      this.isDropdownVisible = false;
    },
    onSelectAnonymous(isAnonymousSelected: boolean) {
      if (this.isAnonymousProfileActive !== isAnonymousSelected) {
        this.lastProfileUsed = isAnonymousSelected ? Activity.Anonymous : Activity.Public;
      }
    },
    onTipsShown() {
      this.closeDropdown();
      this.$modal.show(this.$constants.dynamicModals.OPTIONAL_ANONYMITY_LEARN_MORE_MODAL);
    },
    onCreateProfile() {
      this.closeDropdown();

      this.$modal.show(this.$constants.dynamicModals.WAR_ROOM_USERNAME_AVATAR_MODAL, this.profileCreationParams);
    },
  },
});
